// client/src/coms/workspace/ConversationTabStrip.js
// Open conversations as tabs. Accessible tablist: arrow keys move between tabs, Enter/Space opens, Delete
// closes, Shift+Arrow reorders, the context menu (right click, menu key or Shift+F10) has pin / split / move /
// close actions, and an overflow menu lists every tab when they do not fit. The preview tab (a conversation being
// browsed, replaced by the next one) is shown in italics; double-click or "Keep tab open" keeps it.
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import './workspace.css';

function TabIcon({ item }) {
  if (item.kind === 'dm') return <span aria-hidden="true" className="vx-tab-icon">@</span>;
  if (item.isVoice) return <span aria-hidden="true" className="vx-tab-icon">🔊</span>;
  return <span aria-hidden="true" className="vx-tab-icon">#</span>;
}

function shortServer(name) {
  const s = String(name || '').trim();
  if (!s) return '';
  const parts = s.split(/\s+/).filter(Boolean);
  return (parts.length > 1 ? parts.slice(0, 3).map((p) => p[0]).join('') : s.slice(0, 3)).toUpperCase();
}

function tabLabel(item) {
  const bits = [item.kind === 'dm' ? `Direct message with ${item.title}` : `${item.isVoice ? 'Voice channel' : 'Channel'} ${item.title}`];
  if (item.serverName) bits.push(`in ${item.serverName}`);
  if (item.mentions) bits.push(`${item.mentions} mention${item.mentions === 1 ? '' : 's'}`);
  else if (item.unread) bits.push('unread messages');
  if (item.pinned) bits.push('pinned');
  if (item.preview) bits.push('preview, replaced when you open another conversation');
  return bits.join(', ');
}

export default function ConversationTabStrip({
  items,
  activeId,
  displayed = true,
  secondaryId = null,
  mobile = false,
  splitAvailable = false,
  splitOpen = false,
  onActivate,
  onClose,
  onCloseOthers,
  onMove,
  onPin,
  onKeep = null,
  onOpenInSplit,
  onToggleSplit,
  onReopenClosed,
  canReopen = false,
}) {
  const listRef = useRef(null);
  const [focusId, setFocusId] = useState(activeId);
  const [menu, setMenu] = useState(null); // { id, x, y }
  const [overflowOpen, setOverflowOpen] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [dropIndex, setDropIndex] = useState(-1);
  const menuRef = useRef(null);
  const overflowRef = useRef(null);

  useEffect(() => {
    if (activeId) setFocusId(activeId);
  }, [activeId]);

  // Keep the active tab in view and track whether the strip overflows.
  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return undefined;
    const check = () => setOverflowing(list.scrollWidth > list.clientWidth + 2);
    check();
    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(check);
      ro.observe(list);
    }
    return () => ro && ro.disconnect();
  }, [items.length]);

  useEffect(() => {
    const list = listRef.current;
    if (!list || !activeId) return;
    const el = list.querySelector(`[data-tab-id="${typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(activeId) : activeId}"]`);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [activeId]);

  // Close menus on outside click / Escape (focus goes back to the tab that opened them).
  useEffect(() => {
    if (!menu && !overflowOpen) return undefined;
    const onDown = (e) => {
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      if (overflowRef.current && overflowRef.current.contains(e.target)) return;
      setMenu(null);
      setOverflowOpen(false);
    };
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      const back = menu ? menu.id : null;
      setMenu(null);
      setOverflowOpen(false);
      if (back) focusTab(back);
    };
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('keydown', onKey, true);
    };
  });

  useEffect(() => {
    if (!menu || !menuRef.current) return;
    const first = menuRef.current.querySelector('[role="menuitem"]:not([disabled])');
    if (first) first.focus();
  }, [menu]);

  const focusTab = useCallback((id) => {
    setFocusId(id);
    requestAnimationFrame(() => {
      const list = listRef.current;
      const el = list && list.querySelector(`[data-tab-id="${typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(id) : id}"]`);
      if (el) el.focus();
    });
  }, []);

  const indexOf = (id) => items.findIndex((it) => it.id === id);

  const onKeyDown = (e, item) => {
    const i = indexOf(item.id);
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      e.preventDefault();
      const d = e.key === 'ArrowRight' ? 1 : -1;
      if (e.shiftKey) {
        onMove(item.id, i + d);
        focusTab(item.id);
        return;
      }
      const next = items[(i + d + items.length) % items.length];
      if (next) focusTab(next.id);
    } else if (e.key === 'Home' || e.key === 'End') {
      e.preventDefault();
      const next = e.key === 'Home' ? items[0] : items[items.length - 1];
      if (next) focusTab(next.id);
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onActivate(item.id);
    } else if (e.key === 'Delete' || e.key === 'Backspace') {
      if (item.pinned) return;
      e.preventDefault();
      const neighbour = items[i + 1] || items[i - 1];
      onClose(item.id);
      if (neighbour) focusTab(neighbour.id);
    } else if (e.key === 'ContextMenu' || (e.key === 'F10' && e.shiftKey)) {
      e.preventDefault();
      const r = e.currentTarget.getBoundingClientRect();
      setMenu({ id: item.id, x: r.left, y: r.bottom + 4 });
    }
  };

  const onDragStart = (e, item) => {
    setDragId(item.id);
    try {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', item.id);
    } catch {}
  };

  const onDragOver = (e, item) => {
    if (!dragId) return;
    e.preventDefault();
    const r = e.currentTarget.getBoundingClientRect();
    const before = e.clientX < r.left + r.width / 2;
    setDropIndex(indexOf(item.id) + (before ? 0 : 1));
  };

  const onDrop = (e) => {
    e.preventDefault();
    if (dragId != null && dropIndex >= 0) {
      const from = indexOf(dragId);
      onMove(dragId, dropIndex > from ? dropIndex - 1 : dropIndex);
    }
    setDragId(null);
    setDropIndex(-1);
  };

  const menuItem = (label, action, opts = {}) => (
    <button
      key={label}
      type="button"
      role="menuitem"
      className="vx-menu-item"
      disabled={!!opts.disabled}
      onClick={() => {
        const id = menu ? menu.id : null;
        setMenu(null);
        setOverflowOpen(false);
        action();
        if (id && opts.refocus !== false) focusTab(id);
      }}
    >
      {label}
    </button>
  );

  const menuItemData = menu ? items.find((it) => it.id === menu.id) : null;

  if (!items.length) return null;

  return (
    <div className={`vx-tabstrip${mobile ? ' vx-tabstrip-mobile' : ''}`} data-vx-region="tabs" data-testid="conversation-tabs">
      <div
        ref={listRef}
        role="tablist"
        aria-label="Open conversations"
        className="vx-tablist"
        onDragOver={(e) => dragId && e.preventDefault()}
        onDrop={onDrop}
      >
        {items.map((item, idx) => {
          const isActive = item.id === activeId;
          const shown = isActive && displayed;
          const inSplit = item.id === secondaryId;
          return (
            <React.Fragment key={item.id}>
              {dragId && dropIndex === idx ? <span className="vx-tab-drop" aria-hidden="true" /> : null}
              <div
                role="tab"
                data-tab-id={item.id}
                data-testid="conversation-tab"
                aria-selected={shown}
                aria-label={tabLabel(item)}
                tabIndex={item.id === (focusId || activeId) ? 0 : -1}
                className={[
                  'vx-tab',
                  shown ? 'is-active' : '',
                  isActive && !displayed ? 'is-current' : '',
                  inSplit ? 'is-split' : '',
                  item.pinned ? 'is-pinned' : '',
                  item.preview ? 'is-preview' : '',
                  item.unread ? 'is-unread' : '',
                  dragId === item.id ? 'is-dragging' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                draggable={!mobile}
                onDragStart={(e) => onDragStart(e, item)}
                onDragOver={(e) => onDragOver(e, item)}
                onDragEnd={() => {
                  setDragId(null);
                  setDropIndex(-1);
                }}
                onClick={() => onActivate(item.id)}
                onDoubleClick={() => {
                  if (item.preview && onKeep) onKeep(item.id);
                }}
                onAuxClick={(e) => {
                  if (e.button === 1 && !item.pinned) {
                    e.preventDefault();
                    onClose(item.id);
                  }
                }}
                onContextMenu={(e) => {
                  e.preventDefault();
                  setMenu({ id: item.id, x: e.clientX, y: e.clientY });
                }}
                onKeyDown={(e) => onKeyDown(e, item)}
                onFocus={() => setFocusId(item.id)}
                title={`${item.serverName ? `${item.title} — ${item.serverName}` : item.title}${item.preview ? ' (preview: replaced when you open another conversation; double-click or start typing to keep it)' : ''}`}
              >
                {item.pinned ? <span className="vx-tab-pin" aria-hidden="true">📌</span> : null}
                <TabIcon item={item} />
                <span className="vx-tab-title">{item.title}</span>
                {item.serverName ? (
                  <span className="vx-tab-server" aria-hidden="true">
                    {shortServer(item.serverName)}
                  </span>
                ) : null}
                {item.mentions ? (
                  <span className="vx-tab-mentions" aria-hidden="true">
                    {item.mentions > 99 ? '99+' : item.mentions}
                  </span>
                ) : item.unread ? (
                  <span className="vx-tab-unread" aria-hidden="true" />
                ) : null}
                {inSplit ? (
                  <span className="vx-tab-split-mark" aria-hidden="true" title="Shown in the split pane">
                    ◧
                  </span>
                ) : null}
                {!item.pinned ? (
                  <button
                    type="button"
                    className="vx-tab-close"
                    tabIndex={-1}
                    aria-label={`Close ${item.title}`}
                    data-testid="conversation-tab-close"
                    onClick={(e) => {
                      e.stopPropagation();
                      onClose(item.id);
                    }}
                  >
                    ×
                  </button>
                ) : null}
              </div>
            </React.Fragment>
          );
        })}
        {dragId && dropIndex === items.length ? <span className="vx-tab-drop" aria-hidden="true" /> : null}
      </div>

      <div className="vx-tabstrip-actions">
        {overflowing || canReopen ? (
          <div className="vx-overflow" ref={overflowRef}>
            <button
              type="button"
              className="vx-icon-btn"
              aria-haspopup="menu"
              aria-expanded={overflowOpen}
              aria-label={`All open conversations (${items.length})`}
              title="All open conversations"
              data-testid="conversation-tabs-overflow"
              onClick={() => setOverflowOpen((v) => !v)}
            >
              ⌄<span className="vx-overflow-count">{items.length}</span>
            </button>
            {overflowOpen ? (
              <div className="vx-menu vx-overflow-menu" role="menu" aria-label="Open conversations">
                {items.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    role="menuitemradio"
                    aria-checked={item.id === activeId && displayed}
                    className={`vx-menu-item${item.id === activeId ? ' is-active' : ''}`}
                    onClick={() => {
                      setOverflowOpen(false);
                      onActivate(item.id);
                    }}
                  >
                    <TabIcon item={item} /> {item.title}
                    {item.serverName ? <span className="vx-menu-sub"> · {item.serverName}</span> : null}
                    {item.mentions ? <span className="vx-tab-mentions">{item.mentions}</span> : item.unread ? <span className="vx-tab-unread" /> : null}
                  </button>
                ))}
                {canReopen ? (
                  <>
                    <div className="vx-menu-sep" role="separator" />
                    <button
                      type="button"
                      role="menuitem"
                      className="vx-menu-item"
                      onClick={() => {
                        setOverflowOpen(false);
                        onReopenClosed();
                      }}
                    >
                      Reopen closed tab <span className="vx-kbd">Alt+Shift+T</span>
                    </button>
                  </>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
        {!mobile ? (
          <button
            type="button"
            className={`vx-icon-btn${splitOpen ? ' is-on' : ''}`}
            aria-pressed={splitOpen}
            disabled={!splitAvailable && !splitOpen}
            onClick={onToggleSplit}
            title={splitAvailable || splitOpen ? 'Split view (Alt+Shift+S)' : 'Split view needs a wider window and two open conversations'}
            aria-label="Split view"
            data-testid="split-toggle"
          >
            ◧
          </button>
        ) : null}
      </div>

      {menu && menuItemData ? (
        <div
          ref={menuRef}
          className="vx-menu vx-context-menu"
          role="menu"
          aria-label={`Actions for ${menuItemData.title}`}
          style={{ left: Math.min(menu.x, window.innerWidth - 240), top: Math.min(menu.y, window.innerHeight - 280) }}
          onKeyDown={(e) => {
            if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
            e.preventDefault();
            const els = Array.from(menuRef.current.querySelectorAll('[role="menuitem"]:not([disabled])'));
            const i = els.indexOf(document.activeElement);
            const next = els[(i + (e.key === 'ArrowDown' ? 1 : -1) + els.length) % els.length];
            if (next) next.focus();
          }}
        >
          {menuItemData.preview && onKeep ? menuItem('Keep tab open', () => onKeep(menuItemData.id)) : null}
          {menuItem(menuItemData.pinned ? 'Unpin tab' : 'Pin tab', () => onPin(menuItemData.id, !menuItemData.pinned))}
          {!mobile && !menuItemData.isVoice
            ? menuItem(secondaryId === menuItemData.id ? 'Shown in split view' : 'Open in split view', () => onOpenInSplit(menuItemData.id), { disabled: !splitAvailable || secondaryId === menuItemData.id })
            : null}
          {menuItem('Move left', () => onMove(menuItemData.id, indexOf(menuItemData.id) - 1), { disabled: indexOf(menuItemData.id) === 0 })}
          {menuItem('Move right', () => onMove(menuItemData.id, indexOf(menuItemData.id) + 1), { disabled: indexOf(menuItemData.id) === items.length - 1 })}
          <div className="vx-menu-sep" role="separator" />
          {menuItem('Close tab', () => onClose(menuItemData.id), { disabled: menuItemData.pinned, refocus: false })}
          {menuItem('Close other tabs', () => onCloseOthers(menuItemData.id), { disabled: items.length < 2 })}
        </div>
      ) : null}
    </div>
  );
}
